import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/hooks/useProjects';
import { useProjectStars } from '@/hooks/useProjectStars';
import { ProjectModal } from '@/components/crm/ProjectModal';
import { SharePointSiteInfoModal } from '@/components/crm/SharePointSiteInfoModal';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getDetailedErrorMessage } from '@/lib/errorParser';
import { Project } from '@/types/crm';
import { ProjectLijst, type Weergave } from '@/features/projectlijst/ProjectLijst';
import { useSignalen } from '@/features/projectlijst/hooks';
import {
  STADIA, STANDAARD_FILTER, type LijstFilter, type LijstProject, type Sortering, type Stadium, type StatusFilter,
} from '@/features/projectlijst/data/lijst';

/* /crm/projects. De lijst zelf staat in src/features/projectlijst; hier alleen de
   gegevens, de filter in de URL (zodat terug van een project de lijst herstelt),
   en de dialogen voor nieuw/bewerken en SharePoint. */

const SORTERINGEN: Sortering[] = ['activiteit', 'nummer', 'naam', 'waarde', 'stadium'];
const STATUSSEN: StatusFilter[] = ['lopend', 'afgesloten', 'alles'];

function filterUitUrl(p: URLSearchParams): LijstFilter {
  const stadium = p.get('stage');
  const status = p.get('status');
  const sort = p.get('sort');
  return {
    zoek: p.get('q') ?? '',
    stadium: STADIA.includes(stadium as Stadium) ? (stadium as Stadium) : 'alle',
    status: STATUSSEN.includes(status as StatusFilter) ? (status as StatusFilter) : STANDAARD_FILTER.status,
    sortering: SORTERINGEN.includes(sort as Sortering) ? (sort as Sortering) : STANDAARD_FILTER.sortering,
    oplopend: p.get('dir') === 'asc',
  };
}

function filterNaarUrl(f: LijstFilter): Record<string, string> {
  const uit: Record<string, string> = {};
  if (f.zoek) uit.q = f.zoek;
  if (f.stadium !== 'alle') uit.stage = f.stadium;
  if (f.status !== STANDAARD_FILTER.status) uit.status = f.status;
  if (f.sortering !== STANDAARD_FILTER.sortering) uit.sort = f.sortering;
  if (f.oplopend !== STANDAARD_FILTER.oplopend) uit.dir = f.oplopend ? 'asc' : 'desc';
  return uit;
}

export default function CRMProjects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { projects, loading, refetch, templates, archiveProject } = useProjects();
  const { starredIds, toggleStar } = useProjectStars();
  const signalen = useSignalen();
  const [modal, setModal] = useState<{ open: boolean; project?: Project }>({ open: false });
  const [siteInfo, setSiteInfo] = useState(false);
  const [bezigMetSync, setBezigMetSync] = useState(false);
  const [weergave, setWeergave] = useState<Weergave>(() => {
    try {
      return localStorage.getItem('projectlijst:weergave') === 'bord' ? 'bord' : 'lijst';
    } catch {
      return 'lijst';
    }
  });

  const filter = useMemo(() => filterUitUrl(searchParams), [searchParams]);
  const zetFilter = useCallback((f: LijstFilter) => setSearchParams(filterNaarUrl(f), { replace: true }), [setSearchParams]);

  useEffect(() => {
    try {
      localStorage.setItem('projectlijst:weergave', weergave);
    } catch {
      /* geen opslag: dan is lijst de standaard */
    }
  }, [weergave]);

  const sync = async () => {
    setBezigMetSync(true);
    try {
      const { data, error } = await supabase.functions.invoke('sharepoint-batch-create-folders');
      if (error) throw error;
      const r = data?.results;
      toast({
        title: 'SharePoint-mappen gecontroleerd',
        description: r ? `${r.success} aangemaakt, ${r.alreadyExists} al in orde, ${r.failed} mislukt` : data?.message ?? 'Klaar.',
      });
      refetch();
    } catch (e) {
      toast({ title: 'Controleren mislukt', description: getDetailedErrorMessage(e), variant: 'destructive' });
    } finally {
      setBezigMetSync(false);
    }
  };

  return (
    <>
      <ProjectLijst
        projecten={(projects ?? []) as unknown as LijstProject[]}
        signalen={signalen.data ?? new Map()}
        laden={loading}
        filter={filter}
        onFilter={zetFilter}
        weergave={weergave}
        onWeergave={setWeergave}
        focus={starredIds}
        onFocus={toggleStar}
        onNieuw={() => setModal({ open: true })}
        onBewerk={(p) => setModal({ open: true, project: p as unknown as Project })}
        onArchiveer={archiveProject}
        onSync={sync}
        bezigMetSync={bezigMetSync}
        onSiteInfo={() => setSiteInfo(true)}
      />

      <SharePointSiteInfoModal open={siteInfo} onOpenChange={setSiteInfo} />

      <ProjectModal
        isOpen={modal.open}
        onClose={() => setModal({ open: false })}
        project={modal.project}
        onSaved={() => {
          setModal({ open: false });
          refetch();
        }}
        templates={templates}
      />
    </>
  );
}
